import React, { useState, useEffect, useRef } from 'react';
import { Bug, ExternalLink, Loader2, MessageSquare } from 'lucide-react';
import clsx from 'clsx';

/* Shared per-failed-test Jira control used by both web (TestRunDetail) and
   mobile (MobileTestRunDetail). Caller supplies two thunks:
     - fetchSuggestion() -> axios promise of GET .../jira-suggestion
     - createTicket()    -> axios promise of POST .../jira-ticket
   plus a stable `cacheKey` that changes when the underlying test changes.

   Dedup-aware, suggest-then-confirm:
     - tracked & open  -> just the ticket badge (don't re-raise)
     - regression      -> badge + "File regression ticket"
     - not tracked     -> "Create Jira ticket" -> inline confirm -> create */

function JiraStatusPill({ ticket }) {
  const cat = ticket.status_category || 'new';
  const theme = {
    new: 'bg-slate-100 text-slate-600',
    indeterminate: 'bg-amber-100 text-amber-700',
    done: 'bg-success-100 text-success-700',
  }[cat] || 'bg-slate-100 text-slate-600';
  return (
    <a
      href={ticket.url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
      title={`Open ${ticket.jira_key} in Jira`}
    >
      <span className="font-semibold">{ticket.jira_key}</span>
      {ticket.jira_status && (
        <span className={clsx('px-1.5 py-0.5 rounded-full', theme)}>{ticket.jira_status}</span>
      )}
      <ExternalLink className="h-3 w-3 text-gray-400" />
    </a>
  );
}

// The latest Jira comment on the ticket, mirrored by the backend. Shown inline so
// a triager's "why it failed / what we did" note lands right on the failure — for
// open and closed (Done/Discarded) tickets alike.
function ResolutionNote({ ticket }) {
  if (!ticket || !ticket.resolution_note) return null;
  return (
    <span className="basis-full inline-flex items-start gap-1 mt-0.5 px-2 py-1 text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded-md max-h-28 overflow-y-auto">
      <MessageSquare className="h-3 w-3 text-gray-400 shrink-0 mt-0.5" />
      <span className="min-w-0">
        {ticket.resolution_note_author && (
          <span className="font-medium text-gray-700">{ticket.resolution_note_author}: </span>
        )}
        <span className="whitespace-pre-wrap break-words">{ticket.resolution_note}</span>
      </span>
    </span>
  );
}

export default function JiraTicketAction({ cacheKey, fetchSuggestion, createTicket }) {
  const [sug, setSug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Hold latest thunks in refs so the load effect can depend only on cacheKey
  // (the thunks are fresh arrow functions each render — depending on them would
  // refetch on every parent render).
  const fetchRef = useRef(fetchSuggestion);
  fetchRef.current = fetchSuggestion;
  const createRef = useRef(createTicket);
  createRef.current = createTicket;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setConfirming(false);
    setErr(null);
    fetchRef.current()
      .then((r) => { if (!cancelled) setSug(r.data); })
      .catch(() => { if (!cancelled) setSug(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cacheKey]);

  if (loading || !sug || !sug.jira_configured) return null;

  const ticket = sug.ticket;
  const trackedOpen = ticket && ticket.is_open && !sug.regression;

  const create = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await createRef.current();
      setSug((s) => ({ ...s, tracked: true, suggest: false, regression: false, ticket: r.data }));
      setConfirming(false);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setBusy(false);
    }
  };

  if (trackedOpen) {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-gray-400">Tracked</span>
        <JiraStatusPill ticket={ticket} />
        <ResolutionNote ticket={ticket} />
      </span>
    );
  }

  const label = sug.regression ? 'File regression ticket' : 'Create Jira ticket';

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {ticket && (
        <span className="inline-flex items-center gap-1.5">
          <span className="text-[11px] text-gray-400">{sug.regression ? 'Previously fixed' : 'Tracked'}</span>
          <JiraStatusPill ticket={ticket} />
        </span>
      )}
      {ticket && <ResolutionNote ticket={ticket} />}
      {!confirming ? (
        <button
          onClick={() => setConfirming(true)}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors"
          title="File this failure as a Jira bug"
        >
          <Bug className="h-3 w-3" /> {label}
        </button>
      ) : (
        <span className="inline-flex flex-col gap-1.5 rounded-md border border-primary-200 bg-primary-50/60 p-2 max-w-md">
          <span className="text-[11px] text-gray-600">
            Files a <b>Bug</b> in <b>BA</b>
            {sug.product ? <> · {sug.product}{sug.sub_category ? ` → ${sug.sub_category}` : ''}</> : null}
            {' '}· active sprint · assigned to you.
          </span>
          {sug.proposed_summary && (
            <span className="text-[11px] text-gray-500 italic truncate">“{sug.proposed_summary}”</span>
          )}
          {err && <span className="text-[11px] text-error-700">{err}</span>}
          <span className="inline-flex items-center gap-2">
            <button
              onClick={create}
              disabled={busy}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bug className="h-3 w-3" />}
              {busy ? 'Filing…' : 'Confirm & create'}
            </button>
            <button
              onClick={() => { setConfirming(false); setErr(null); }}
              disabled={busy}
              className="text-[11px] text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </span>
        </span>
      )}
    </span>
  );
}
